import React, { FC, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useMediaQuery } from '@mui/material';
import { useAppSelector } from '../../hooks/redux';
import { useCustomSnackbar } from '../../hooks/useCustomEnqueueSnackbar';
import { useDateLocalization } from '../../hooks/useDateLocalization';
import { surveyApi } from '../../services/apiServices/user/survey';
import { CustomMUIButton } from '../../components/commons/buttons/CustomMUIButton';
import { FormInputTextArea } from '../../components/inputs/FormInputTextArea';
import { FormStarRating } from '../../components/inputs/FormStarRating';
import { SurveyUnavailable } from './SurveyUnavailable';
import { getErrorMessage } from '../../utils';
import { ButtonClassNames, GlobalRouteNames, SuccessRouteNames } from '../../models/Common';
import { ApiError } from '../../models/ApiError';
import './UserSurvey.scss';

interface UserSurveyForm {
  [key: `question-${number}`]: number | undefined;
  proposals: string | undefined;
}

export const UserSurvey: FC = () => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const history = useHistory();
  const { formatDate } = useDateLocalization();
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const isMobile = useMediaQuery('(max-width:767px)');
  const queryParams = new URLSearchParams(search);
  const { isAuth } = useAppSelector((state) => state.authReducer);
  const { selectedLanguage } = useAppSelector((state) => state.langReducer);
  const hasRequiredParams = queryParams.has('id') && queryParams.has('userId') && queryParams.has('orderId');
  const {
    data: surveyData,
    isFetching: isSurveyDataLoading,
    isError: isSurveyError,
    error: surveyError,
  } = surveyApi.useGetFormConfigQuery(
    {
      id: String(queryParams.get('id')),
      userId: String(queryParams.get('userId')),
      orderId: String(queryParams.get('orderId')),
      lang: selectedLanguage,
    },
    { skip: !hasRequiredParams, refetchOnMountOrArgChange: true },
  );
  const [sendForm, { isLoading: isSending }] = surveyApi.useSendSurveyFormMutation();
  const methods = useForm<UserSurveyForm>();
  const { handleSubmit } = methods;

  useEffect(() => {
    if (isSurveyError && surveyError) {
      const err = surveyError as ApiError;
      if (err.status === 409) {
        history.replace(`${GlobalRouteNames.SuccessPublicPage}${SuccessRouteNames.SurveySent}`);
      }
    }
  }, [isSurveyError, surveyError]);

  const handleClose = () => {
    const path = isAuth ? GlobalRouteNames.HomePage : GlobalRouteNames.LoginPage;
    history.replace(path);
  };

  const handleRate = async (data: UserSurveyForm) => {
    try {
      const output = {
        rates: Object.entries(data)
          .filter(([key]) => key !== 'proposals')
          .map(([key, value]) => ({
            QuestionID: key.split('-')[1],
            Rate: value || 0,
          })),
        commonReview: data.proposals ? data.proposals.trim() : '',
      };
      if (surveyData?.id) {
        await sendForm({
          body: output,
          id: surveyData.id,
          orderId: String(queryParams.get('orderId')),
          userId: String(queryParams.get('userId')),
        }).unwrap();
        history.replace(`${GlobalRouteNames.SuccessPublicPage}${SuccessRouteNames.SurveySuccess}`);
      }
    } catch (e) {
      const message = getErrorMessage(e);
      customEnqueueSnackbar(message, 'error');
    }
  };

  if (
    !hasRequiredParams ||
    (hasRequiredParams && isSurveyError && surveyError && (surveyError as ApiError).status !== 409)
  ) {
    return <SurveyUnavailable />;
  }

  return (
    <div className="user-survey-page page">
      <div className="user-survey-page__container container">
        <div className={`user-survey-form ${isSurveyDataLoading ? 'skeleton' : 'skeleton--disabled'}`}>
          <div className="card card__primary card__primary_full-mobile user-survey-form__container">
            <FormProvider {...methods}>
              <form className="user-survey-form__container__form" onSubmit={handleSubmit(handleRate)}>
                <article className="user-survey-form__container__form__title">
                  {surveyData?.Name}{' '}
                  {surveyData?.ExecDate
                    ? t('qualityAssessment.from', {
                        date: formatDate(surveyData.ExecDate, {
                          day: '2-digit',
                          month: '2-digit',
                          year: '2-digit',
                          locale: 'uk',
                        }),
                      })
                    : ''}
                </article>
                {surveyData?.question?.length
                  ? surveyData.question.map((question) => {
                      return (
                        <FormStarRating
                          key={question.id}
                          name={`question-${question.id}`}
                          label={question.Question}
                          defaultRate={0}
                        />
                      );
                    })
                  : null}
                <FormInputTextArea
                  name="proposals"
                  label={t('qualityAssessment.commentsSuggestions')}
                  className="primary"
                  maxLength={200}
                  rows={3}
                />
                <div className="modals__btns-wrapper">
                  <div className="modals__btn-group" style={{ order: Number(isMobile) }}>
                    <CustomMUIButton
                      label={t('qualityAssessment.close')}
                      handleClick={handleClose}
                      className={ButtonClassNames.SecondaryOutlined}
                      isBold
                      type="button"
                    />
                  </div>
                  <div className="modals__btn-group" style={{ order: Number(!isMobile) }}>
                    <CustomMUIButton
                      label={t('qualityAssessment.send')}
                      type="submit"
                      className={ButtonClassNames.PrimaryFulfilled}
                      isBold
                      isLoading={isSending}
                    />
                  </div>
                </div>
              </form>
            </FormProvider>
          </div>
        </div>
      </div>
    </div>
  );
};
